/**
 * FeedbackModal.js — modal opened by the floating FeedbackButton.
 *
 * Mirrors CaptureEditModal's hand-rolled overlay pattern (no shared Modal
 * primitive in this codebase): fixed-inset backdrop, centered white card,
 * header with × close, scrollable body, footer with helper text + Cancel
 * + Submit. ESC and backdrop-click close when not saving.
 *
 * Submit flow (Q5A — inline error banner so the user's typed text survives
 * a failed network call):
 *
 *   [user clicks Submit]
 *         │
 *         ▼
 *   POST /api/feedback
 *         │
 *   ┌─────┴───────────────────┐
 *   200                     4xx / 5xx / network
 *   │                          │
 *   ▼                          ▼
 *  showToast(success)       inline red banner above footer
 *  close modal ~1s later    Submit re-enabled
 *
 * page_url and page_context come from props (FeedbackButton parses them at
 * open-time so the captured URL is the page the user was on when they
 * clicked, not whatever they navigated to between click and submit).
 */

import React, { useEffect, useRef, useState } from 'react';
import { colors } from './AppLayout';
import { useToast } from './ToastHost';
import { useAuth } from '../AuthContext';
import { API_BASE } from '../config';


const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Bug: something is broken' },
  { value: 'feature', label: 'Feature request' },
  { value: 'question', label: 'Question' },
  { value: 'other', label: 'Other' },
];

const MAX_FEEDBACK_LEN = 5000;


function FeedbackModal({ open, onClose, pageUrl, pageContext }) {
  // NOTE: these style objects live INSIDE the component (not at module
  // top-level) because AppLayout imports FeedbackButton, which imports
  // FeedbackModal — a circular import. Reading `colors` from AppLayout at
  // module-init time gets an uninitialized binding and throws TypeError,
  // killing the entire React bundle on every route. Evaluating them per
  // render is cheap and breaks the cycle.
  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.mediumGray}`,
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    color: colors.darkText,
    boxSizing: 'border-box',
    background: colors.white,
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: colors.textMuted,
    marginBottom: '4px',
  };

  const { authFetch } = useAuth();
  const { showToast } = useToast();

  const [feedbackType, setFeedbackType] = useState('bug');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Reset state every time the modal opens so leftover text from a previous
  // failed submit doesn't carry over into a brand-new feedback session.
  useEffect(() => {
    if (!open) return;
    setFeedbackType('bug');
    setFeedbackText('');
    setErrorMsg(null);
    setSubmitting(false);
  }, [open]);

  // Focus moves into the form when it opens and back to what had it when it closes; Tab
  // stays inside the form while it is open (Lane 1382, Codex r2: opened from the deck
  // chat's header, focus stayed on the Feedback button inside the chat, so Escape closed
  // the chat behind the form instead of the form).
  const dialogRef = useRef(null);
  const textRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    if (textRef.current) textRef.current.focus();
    return () => {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus();
    };
  }, [open]);
  const keepTabInside = (e) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = [...dialogRef.current.querySelectorAll('button, select, textarea, input, [href]')]
      .filter((el) => !el.disabled);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  // ESC closes when not submitting.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, submitting, onClose]);

  const trimmedLen = feedbackText.trim().length;
  const overLimit = feedbackText.length > MAX_FEEDBACK_LEN;
  const canSubmit = !submitting && trimmedLen > 0 && !overLimit;

  let helperText = '';
  if (!canSubmit && !submitting) {
    if (trimmedLen === 0) helperText = 'Please enter your feedback.';
    else if (overLimit) helperText = `Too long. Max ${MAX_FEEDBACK_LEN} characters.`;
  }

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMsg(null);

    const body = {
      feedback_type: feedbackType,
      feedback_text: feedbackText.trim(),
      page_url: pageUrl || window.location.href,
      page_context: pageContext || {},
    };

    let response;
    try {
      response = await authFetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      console.log('[FEEDBACK-SUBMIT-NETWORK]', err?.message || String(err));
      setErrorMsg('Network error. Try again.');
      setSubmitting(false);
      return;
    }

    if (!isMountedRef.current) return;

    if (response.ok) {
      showToast({
        message: 'Thanks, we got it.',
        variant: 'success',
        durationMs: 3000,
      });
      // Brief delay before close so the user sees Submit settle, matching
      // the "close modal on success after ~1 second" requirement.
      setTimeout(() => {
        if (isMountedRef.current) onClose();
      }, 800);
      return;
    }

    let detail = null;
    try {
      const data = await response.json();
      detail = data?.detail;
    } catch (parseErr) {
      console.log('[FEEDBACK-SUBMIT-PARSE-ERROR]', parseErr?.message || String(parseErr));
    }

    if (response.status >= 400 && response.status < 500) {
      console.log('[FEEDBACK-SUBMIT-4xx]', response.status, detail);
      setErrorMsg(typeof detail === 'string' ? detail : "Couldn't submit. Please check your input.");
    } else {
      console.log('[FEEDBACK-SUBMIT-5xx]', response.status, detail);
      setErrorMsg("Couldn't submit. Try again.");
    }
    setSubmitting(false);
  };

  if (!open) return null;

  const counterColor = overLimit ? colors.danger : colors.textMuted;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        ref={dialogRef}
        onKeyDown={keepTabInside}
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
        style={{
          background: colors.white,
          borderRadius: '12px',
          width: '90%',
          maxWidth: '520px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: `1px solid ${colors.mediumGray}`,
          flexShrink: 0,
        }}>
          <h2
            id="feedback-modal-title"
            style={{ margin: 0, fontSize: '16px', color: colors.darkText, fontWeight: 600 }}
          >
            Send feedback
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              color: colors.textMuted,
              cursor: submitting ? 'not-allowed' : 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={labelStyle}>Type</div>
            <select
              value={feedbackType}
              onChange={(e) => setFeedbackType(e.target.value)}
              disabled={submitting}
              style={inputStyle}
            >
              {FEEDBACK_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <div style={labelStyle}>Feedback</div>
            <textarea
              ref={textRef}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              disabled={submitting}
              placeholder="What happened, what would help, or what's confusing..."
              rows={7}
              style={{
                ...inputStyle,
                resize: 'vertical',
                minHeight: '120px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                lineHeight: 1.5,
              }}
            />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            fontSize: '11px',
            color: counterColor,
            marginBottom: '12px',
          }}>
            {feedbackText.length} / {MAX_FEEDBACK_LEN}
          </div>

          {errorMsg && (
            <div style={{
              padding: '8px 12px',
              background: '#FEE2E2',
              border: `1px solid ${colors.danger}`,
              borderRadius: '6px',
              color: '#7F1D1D',
              fontSize: '13px',
            }}>
              {errorMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px',
          borderTop: `1px solid ${colors.mediumGray}`,
          flexShrink: 0,
          gap: '12px',
        }}>
          <span style={{
            fontSize: '12px',
            color: colors.textMuted,
            flex: 1,
            minWidth: 0,
          }}>
            {helperText}
          </span>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                background: 'transparent',
                border: `1px solid ${colors.mediumGray}`,
                color: colors.darkText,
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                background: canSubmit ? colors.electricBlue : colors.mediumGray,
                border: `1px solid ${canSubmit ? colors.electricBlue : colors.mediumGray}`,
                color: canSubmit ? colors.white : colors.textMuted,
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Sending…' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;
